import { Component, ElementRef, EventEmitter, Input, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MsgModel } from 'src/app/component/common-components/msg/model/msg.model';
import { QuestionHelpText } from 'src/app/model/question-help-text';
import { Student } from 'src/app/model/student/student.model';
import { GetPropertyPipe } from 'src/app/pipes/get-property.pipe';
import { QuestionHelpTextService } from 'src/app/service/question-help-text.service';
import { CommunicationEventPurposePropertyTypes, StudentCommunicationEvent } from '../../model/student-communication-events';
import { StudentCommunicationEventsService } from '../../service/student-communication-events.service';
import { GroupByPipe } from 'src/app/pipes/group-by.pipe';
import { HelperService } from 'src/app/service/helper.service';
import { Region } from 'src/app/model/region.model';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { CleanDatePipe } from 'src/app/pipes/clean-date.pipe';
import { TitleCasePipe, ViewportScroller } from '@angular/common';
import { MatPaginator } from '@angular/material/paginator';

@Component({
    selector: 'search-child-communication-events',
    templateUrl: './search-child-communication-events.component.html',
    styleUrls: ['./search-child-communication-events.component.scss']
})
export class SearchChildCommunicationEventsComponent implements OnInit {
    @Input() student: Student;
    @Input() shoppingCartStudents: Student[];
    @Output() returnOutput: EventEmitter<any> = new EventEmitter<any>();

    @ViewChild('searchChildCommunicationEventsElement') searchChildCommunicationEventsElement: ElementRef;
    @ViewChild('matchingCommunicationEventsElement') matchingCommunicationEventsElement: ElementRef;
    @ViewChild('actionCommunicationEventsElement') actionCommunicationEventsElement: ElementRef;

    communicationEventPurposeTypes: CommunicationEventPurposePropertyTypes[];

    title: string = '';
    mode: string = '';
    userRegion: Region;

    isLoading: boolean = false;
    isSearchedLoading: boolean = false;
    isDeleting: boolean = false;

    promises: Array<any> = [];

    // Table display
    studentCommunicationEventColumns: string[] = [
        //'SEARCH',
        'CREATED_DATE',
        'MODIFIED_DATE',
        'COMMUNICATION_DATE',
        'COMMUNICATION_EVENT_PURPOSE_TYPES.TYPE',
        'COMMENTS'
    ];
    searchedStudentCommunicationEventColumns: string[] = [
        'EDIT',
        'STUDENT.MSD_NUMBER',
        'STUDENT.PERSON.LAST_NAME',
        'CREATED_DATE',
        'MODIFIED_DATE',
        'COMMUNICATION_DATE',
        'COMMUNICATION_EVENT_PURPOSE_TYPES.TYPE',
        'COMMENTS'
    ];

    // Table results
    @ViewChildren(MatSort) sorts: QueryList<MatSort> = new QueryList<MatSort>();
    @ViewChildren(MatPaginator) paginators: QueryList<MatPaginator> = new QueryList<MatPaginator>();
    regions: any[] = [];
    results: StudentCommunicationEvent[] = [];

    searchedEvent: any;
    searchedRegions: any[] = [];
    searchedResults: StudentCommunicationEvent[] = [];
    searchedSelectCount: number = 0;
    searchedFilters: any;

    searchedShoppingCart: StudentCommunicationEvent[] = [];
    searchedShoppingCartStudents: Student[] = [];

    globalSortIndex: number = 0;

    msg: MsgModel;
    createMsg: MsgModel;

    // Help Text
    lstQuestionHelpText: QuestionHelpText[] = [];

    getPropertyPipe = new GetPropertyPipe();
    groupByPipe = new GroupByPipe();
    cleanDatePipe = new CleanDatePipe();
    titlecasePipe = new TitleCasePipe();

    constructor(
        private dialog: MatDialog,
        public questionHelpTextService: QuestionHelpTextService,
        private studentCommunicationEventsService: StudentCommunicationEventsService,
        private helperService: HelperService,
        private viewportScroller: ViewportScroller
    ) {}

    ngOnInit(): void {
        this.isLoading = true;

        this.title = this.getStudentName(this.student) + ' Communication Events';

        // Reference API calls
        this.getCommunicationEventPurposeTypes();
        this.getCommunicationEvents();
        this.getUserRegion();

        Promise.all(this.promises).then((res) => {
            this.isLoading = false;
        });
    }

    getStudentName(student: Student) {
        let name: string = '';
        name += student.PERSON.FIRST_NAME + ' ';
        name += student.PERSON.LAST_NAME;

        name = this.titlecasePipe.transform(name);
        return name;
    }

    getUserRegion() {
        let deferred = new Promise((resolve, reject) => {
            this.helperService.lstUserProfileRegion$.subscribe((userRegion: Region[]) => {
                this.userRegion = userRegion[0];

                resolve(this.userRegion);
            });
        });

        this.promises.push(deferred);
    }

    getCommunicationEvents() {
        this.isLoading = true;
        this.searchedResults = [];
        this.searchedRegions = [];

        let deferred = new Promise((resolve, reject) => {
            this.studentCommunicationEventsService.getStudentCommunicationEvents(this.student.ID!.toString()).subscribe((res) => {
                if (res.studentCommunicationEvents) {
                    this.results = res.studentCommunicationEvents;

                    this.cleanResultsDates();
                } else if (res.msg) {
                    this.results = [];
                    this.msg = res.msg;
                } else {
                    this.results = [];
                    this.msg = { SUCCESS: false, MSG: 'Error retrieving Communication Events.' };
                }

                this.isLoading = false;
                this.updateDataSource();

                setTimeout(() => {
                    const targetElement = this.searchChildCommunicationEventsElement.nativeElement;
                    targetElement.scrollIntoView({ behavior: 'smooth' });
                }, 200);
                resolve(res);
            });
        });

        this.promises.push(deferred);
    }

    getCommunicationEventPurposeTypes() {
        let deferred = new Promise((resolve, reject) => {
            this.studentCommunicationEventsService.getCommunicationEventPurposeTypes().subscribe((res) => {
                if (res.communicationEventPurposeTypes) {
                    this.communicationEventPurposeTypes = res.communicationEventPurposeTypes;
                    resolve(res);
                } else if (res?.msg) {
                    reject(res);
                } else {
                    reject('err');
                }
            });
        });

        this.promises.push(deferred);
    }

    cleanResultsDates() {
        this.results.forEach((res) => {
            res.COMMUNICATION_DATE = this.cleanDatePipe.transform(res.COMMUNICATION_DATE);
            res.CREATED_DATE = this.cleanDatePipe.transform(res.CREATED_DATE!);
            res.MODIFIED_DATE = this.cleanDatePipe.transform(res.MODIFIED_DATE!);
        });
    }
    cleanSearchedResultsDates() {
        this.searchedResults.forEach((res) => {
            res.COMMUNICATION_DATE = this.cleanDatePipe.transform(res.COMMUNICATION_DATE);
            res.CREATED_DATE = this.cleanDatePipe.transform(res.CREATED_DATE!);
            res.MODIFIED_DATE = this.cleanDatePipe.transform(res.MODIFIED_DATE!);
        });
    }

    updateDataSource() {
        this.globalSortIndex = 0;

        setTimeout(() => {
            this.regions = this.getRegions(this.results);

            setTimeout(() => {
                this.regions.forEach((region, index) => {
                    let dataSource = new MatTableDataSource<StudentCommunicationEvent>();
                    dataSource.data = region.STUDENT_COMMUNICATION_EVENTS.sort((a: any, b: any) => (a.COMMUNICATION_DATE! > b.COMMUNICATION_DATE! ? -1 : 1));

                    // Paginator
                    dataSource.paginator = this.paginators.toArray()[this.globalSortIndex];

                    dataSource.sort = this.sorts.toArray()[this.globalSortIndex];
                    const defaultSort: Sort = { active: 'COMMUNICATION_DATE', direction: 'desc' };
                    dataSource.sort.active = defaultSort.active;
                    dataSource.sort.direction = defaultSort.direction;
                    dataSource.sortingDataAccessor = (obj, property) => this.getPropertyPipe.transform(obj, property, true);

                    region.tableDataSource = dataSource;

                    this.globalSortIndex += 1;

                    setTimeout(() => {
                        region.tableDataSource.sort?.sortChange.emit(defaultSort);
                    });
                });
            });
        });
    }

    getRegions(studentCommunicationEvents: StudentCommunicationEvent[]) {
        let regions: any[] = [];

        studentCommunicationEvents.forEach((sce) => {
            let regionIndex = regions.findIndex((r) => r.CODE === sce.REGION.CODE);

            if (regionIndex === -1) {
                let region: any = JSON.parse(JSON.stringify(sce.REGION));
                region.STUDENT_COMMUNICATION_EVENTS = [];
                region.STUDENT_COMMUNICATION_EVENTS.push(sce);

                regions.push(region);
            } else {
                regions[regionIndex].STUDENT_COMMUNICATION_EVENTS.push(sce);
            }
        });

        // Set user's region to top
        if (this.userRegion) {
            regions = regions.sort((a, b) => (a.CODE! > b.CODE! ? 1 : -1));
            let userRegionIndex = regions.findIndex((r) => r.CODE === this.userRegion.CODE);

            if (userRegionIndex >= 0) {
                regions.splice(0, 0, regions.splice(userRegionIndex, 1)[0]);
            }
        } else {
            regions = regions.sort((a, b) => (a.CODE! > b.CODE! ? 1 : -1));
        }

        return regions;
    }

    getStudentsWithSameCommunicationEvent(event: any) {
        this.searchedEvent = event;
        let studentCommunicationEvent = event.studentCommunicationEvent;
        this.searchedFilters = event.filters;
        let searchType = event.searchType; // all or selected
        let payload = {
            matchComment: this.searchedFilters['Match Comment'].checked,
            matchCreatedDate: this.searchedFilters['Match Created Date'].checked,
            matchCreator: this.searchedFilters['Match Creator'].checked,
            matchRegion: this.searchedFilters['Match Region'].checked,
            matchUpdatedDate: this.searchedFilters['Match Updated Date'].checked,
            matchUpdater: this.searchedFilters['Match Updater'].checked,
            studentCommunicationEvent: studentCommunicationEvent
        };

        if (searchType) {
            this.isSearchedLoading = true;
            const targetElement = this.matchingCommunicationEventsElement.nativeElement;
            targetElement.scrollIntoView({ behavior: 'smooth' });

            this.studentCommunicationEventsService.getStudentsWithSameCommunicationEvent(payload).subscribe((res) => {
                if (res.studentCommunicationEvents) {
                    if (searchType === 'selected') {
                        this.searchedResults = res.studentCommunicationEvents.filter((sce: any) =>
                            this.shoppingCartStudents.some((s: any) => s.ID === sce.STUDENT.ID)
                        );
                    } else {
                        // all
                        this.searchedResults = res.studentCommunicationEvents;
                    }

                    this.searchedShoppingCart = [];
                    this.searchedShoppingCartStudents = [];
                    this.searchedSelectCount = 0;

                    this.cleanSearchedResultsDates();
                    this.updateSearchedDataSource();

                    setTimeout(() => {
                        const targetElement = this.matchingCommunicationEventsElement.nativeElement;
                        targetElement.scrollIntoView({ behavior: 'smooth' });
                    }, 100);
                } else if (res.msg) {
                    this.results = [];
                    this.msg = res.msg;
                } else {
                    this.results = [];
                    this.msg = { SUCCESS: false, MSG: 'Error retrieving Communication Events.' };
                }

                this.isSearchedLoading = false;
            });
        }
    }

    updateSearchedDataSource() {
        this.globalSortIndex = 1;
        setTimeout(() => {
            this.searchedRegions = this.getRegions(this.searchedResults);

            setTimeout(() => {
                this.searchedRegions.forEach((region, index) => {
                    let dataSource = new MatTableDataSource<any>();
                    dataSource.data = region.STUDENT_COMMUNICATION_EVENTS.sort((a: any, b: any) => (a.COMMUNICATION_DATE! > b.COMMUNICATION_DATE! ? -1 : 1));

                    // Paginator
                    dataSource.paginator = this.paginators.toArray()[this.globalSortIndex];

                    dataSource.sort = this.sorts.toArray()[this.globalSortIndex];
                    const defaultSort: Sort = { active: 'STUDENT.PERSON.LAST_NAME', direction: 'asc' };
                    dataSource.sort.active = defaultSort.active;
                    dataSource.sort.direction = defaultSort.direction;
                    dataSource.sortingDataAccessor = (obj, property) => this.getPropertyPipe.transform(obj, property, true);

                    region.tableDataSource = dataSource;

                    this.globalSortIndex += 1;

                    setTimeout(() => {
                        region.tableDataSource.sort?.sortChange.emit(defaultSort);
                    });
                });
            });
        });
    }

    toggleSearchedResult(studentCommunicationEvent: any) {
        studentCommunicationEvent.SELECTED = !studentCommunicationEvent.SELECTED;

        if (studentCommunicationEvent.SELECTED) {
            this.searchedSelectCount++;
            this.searchedShoppingCart.push(studentCommunicationEvent);
        } else {
            this.searchedSelectCount--;
            this.searchedShoppingCart = this.searchedShoppingCart.filter((cart) => cart.ID !== studentCommunicationEvent.ID);
        }

        this.resetMode();
    }

    selectAllSearchedResults() {
        this.searchedSelectCount = 0;
        this.searchedShoppingCart = [];

        this.searchedRegions.forEach((region, index) => {
            region.tableDataSource.data.forEach((sce: any) => {
                sce.SELECTED = true;
                this.searchedShoppingCart.push(sce);
            });

            this.searchedSelectCount += region.tableDataSource.data.length;
        });

        this.resetMode();

        setTimeout(() => {
            const targetElement = this.actionCommunicationEventsElement.nativeElement;
            targetElement.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    }

    removeAllSearchedResults() {
        this.searchedSelectCount = 0;
        this.searchedShoppingCart = [];

        this.searchedRegions.forEach((region, index) => {
            region.tableDataSource.data.forEach((sce: any) => {
                sce.SELECTED = false;
            });
        });

        this.resetMode();
    }

    setMode(mode: string) {
        if (mode === 'create') {
            this.searchedShoppingCartStudents = this.searchedShoppingCart.map((cart) => {
                return cart.STUDENT;
            });
        } else if (mode === 'update') {
        }

        this.mode = mode;
    }

    resetMode() {
        this.mode = '';
    }

    createCommunicationEventsOutput(event: any) {
        if (event.success) {
            this.resetMode();
            this.createMsg = new MsgModel();
            this.createMsg.MSG = 'Successfully created communication events.';
            this.createMsg.SUCCESS = true;

            this.isLoading = true;
            this.getCommunicationEvents();
            Promise.all(this.promises).then((res) => {
                this.updateDataSource();
                this.removeAllSearchedResults();
                this.isLoading = false;
            });

            setTimeout(() => {
                this.createMsg = new MsgModel();
            }, 5000);
        } else if (event.canceled) {
            this.resetMode();
        }
    }

    editCommunicationEventsOutput(event: any) {
        if (event.success) {
            this.resetMode();
            this.createMsg = new MsgModel();
            this.createMsg.MSG = 'Successfully updated communication events.';
            this.createMsg.SUCCESS = true;

            this.isLoading = true;
            this.getCommunicationEvents();
            Promise.all(this.promises).then((res) => {
                this.updateDataSource();
                this.removeAllSearchedResults();
                this.isLoading = false;
            });

            setTimeout(() => {
                this.createMsg = new MsgModel();
            }, 5000);
        } else if (event.canceled) {
            this.resetMode();
        }
    }

    deleteSelectedStudentCommunicationEvents() {
        this.isDeleting = true;

        this.studentCommunicationEventsService.deleteStudentCommunicationEvents(this.searchedShoppingCart).subscribe((res) => {
            if (res.msg) {
                if (res.msg.SUCCESS) {
                    this.resetMode();
                    this.isLoading = true;
                    this.getCommunicationEvents();
                    Promise.all(this.promises).then((res) => {
                        this.updateDataSource();
                        this.removeAllSearchedResults();
                        this.isLoading = false;
                    });
                } else {
                    this.msg = { SUCCESS: false, MSG: 'Error deleting Communication Events.' };
                }
            } else if (res.msg) {
                this.msg = res.msg;
            } else {
                this.msg = { SUCCESS: false, MSG: 'Error deleting Communication Events.' };
            }

            this.isDeleting = false;
        });
    }
}
